import {
  getDefaultFilterParams,
  inboundDefaultFilters,
} from "../constants/filters/inboundDefaultFilters";
import { LaneType } from "../types/outbound/lane.type";
import { useSelectedServiceCenter } from "./useSelectedServiceCenter";
import { SELECTED_DATE_PARAM_KEY } from "./useDateSearchParamOrFallbackToToday";
import { UNPLANNED_SELECTED_DATE } from "./useUnplannedDatetimeSearchParam";

type InboundProps = {
  planDate: string;
  unplannedDateTime?: string;
  sicCode?: string;
};

type OutboundShipmentProps = {
  view: string;
  tripNumber?: number;
  plannedPullId?: number;
  etaDate?: string;
  trapId?: number;
};

// These URLs vary based on the service center, so we need to get the service center from the context
export function usePageUrls() {
  const [serviceCenter] = useSelectedServiceCenter();

  return {
    inboundPlanning: ({
      planDate,
      unplannedDateTime,
      sicCode,
    }: InboundProps) => {
      const searchParams = new URLSearchParams();

      searchParams.append(SELECTED_DATE_PARAM_KEY, planDate);

      if (unplannedDateTime) {
        searchParams.append("unplannedDateTime", unplannedDateTime);
      }

      const url = `/operations/${sicCode ?? serviceCenter.code}/inbound/planning/list?${searchParams.toString()}`;

      return url;
    },
    inboundUrgentAndCriticalCare: ({ planDate, sicCode }: InboundProps) => {
      const searchParams = new URLSearchParams();

      searchParams.append(SELECTED_DATE_PARAM_KEY, planDate);
      return `/operations/${sicCode ?? serviceCenter.code}/inbound/uc-cc?${searchParams.toString()}`;
    },
    inboundPrePlanning: ({
      planDate,
      unplannedDateTime,
      sicCode,
    }: InboundProps) => {
      const searchParams = new URLSearchParams();
      searchParams.append(SELECTED_DATE_PARAM_KEY, planDate);
      if (unplannedDateTime) {
        searchParams.append(UNPLANNED_SELECTED_DATE, unplannedDateTime);
      }

      const url = `/operations/${sicCode ?? serviceCenter.code}/inbound/pre-planning/appointments?${searchParams.toString()}`;
      return url;
    },

    inboundUnplannedFreight: ({
      isNewDesign,
      planDate,
      unplannedDateTime,
      sicCode,
      // isNewDesign prop is temporary until the FF being on PROD
    }: InboundProps & { isNewDesign: boolean }) => {
      const searchParams = new URLSearchParams();

      searchParams.append(SELECTED_DATE_PARAM_KEY, planDate);

      if (unplannedDateTime) {
        searchParams.append(UNPLANNED_SELECTED_DATE, unplannedDateTime);
      }

      const defaultFilters = !isNewDesign
        ? getDefaultFilterParams(inboundDefaultFilters["unplanned-freight"])
        : "";

      const defaultFilterParams = new URLSearchParams(defaultFilters);

      defaultFilterParams.forEach((value, key) => {
        searchParams.append(key, value);
      });

      const newUrl = `/operations/${sicCode ?? serviceCenter.code}/inbound/unplanned-freight/linehaul?${searchParams.toString()}`;
      const oldUrl = `/operations/${sicCode ?? serviceCenter.code}/inbound/unplanned-freight?${searchParams.toString()}`;

      return isNewDesign ? newUrl : oldUrl;
    },
    outboundLaneDetails: (sicCode?: string) =>
      `/operations/${sicCode ?? serviceCenter.code}/outbound/lane-details/?view=lane`,
    outboundFreightDetailsDoorView: `/operations/${serviceCenter.code}/outbound/lane-details/?view=door`,
    outboundShipmentDetails: (
      loadToSicId: number,
      loadToSicCode: string,
      laneType: LaneType
    ) =>
      `/operations/${serviceCenter.code}/outbound/lane-details/shipment-details?view=lane&loadToSicId=${loadToSicId}&loadToSicCode=${loadToSicCode}&laneType=${laneType}`,
    outboundShipmentDetailsManageDoors: (
      loadToSicId: number,
      loadToSicCode: string,
      laneType: LaneType
    ) =>
      `/operations/${serviceCenter.code}/outbound/lane-details/shipment-details?view=lane&loadToSicId=${loadToSicId}&loadToSicCode=${loadToSicCode}&laneType=${laneType}&laneTab=manageDoors`,
    outboundShipmentDetailsByDoor: (
      doorNumber: number,
      loadToSicId: number,
      loadToSicCode: string,
      laneType: LaneType
    ) =>
      `/operations/${serviceCenter.code}/outbound/lane-details/shipment-details?view=door&viewDoorNumber=${doorNumber}&loadToSicId=${loadToSicId}&loadToSicCode=${loadToSicCode}&laneType=${laneType}`,
    outboundTrailerShipments: ({
      view,
      tripNumber,
      plannedPullId,
      trapId,
    }: OutboundShipmentProps) =>
      `/operations/${serviceCenter.code}/outbound/trailer-hub/shipments?view=${view}${trapId ? `&trapId=${trapId}` : ""}${tripNumber ? `&tripNumber=${tripNumber}` : ""}${plannedPullId ? `&plannedPullId=${plannedPullId}` : ""}`,
    outboundTrailers: (view: string) =>
      `/operations/${serviceCenter.code}/outbound/trailer-hub?&view=${view}`,
    zonesListInbound: `/settings/${serviceCenter.code}/inbound/zones/list?route=all&serviceCenterId=${serviceCenter.id}`,
    settings: (sicCode?: string) =>
      `/settings/${sicCode ?? serviceCenter.code}/`,
    dispatch: (sicCode?: string) =>
      `/operations/${sicCode ?? serviceCenter.code}/dispatch`,
    equipmentInventory: (sicCode?: string) =>
      `/operations/${sicCode ?? serviceCenter.code}/equipment-inventory/live-inventory`,
    tripList: (sicCode?: string) =>
      `/operations/${sicCode ?? serviceCenter.code}/trip-list`,
  };
}
